import type { Folder } from "@hq/contracts";
import { newId } from "./ids";
import type { NowContext } from "./time";

export function createFolder(
  input: { id?: string; name: string; parentId?: string; sortOrder?: number },
  ctx: NowContext,
): Folder {
  const name = input.name.trim();
  if (!name) throw new Error("Folder name is required");
  const folder: Folder = {
    id: input.id ?? newId(),
    createdAt: ctx.now,
    updatedAt: ctx.now,
    revision: 0,
    name,
    sortOrder: input.sortOrder ?? Date.parse(ctx.now),
  };
  if (input.parentId) folder.parentId = input.parentId;
  return folder;
}

export function renameFolder(folder: Folder, name: string, ctx: NowContext): Folder {
  const n = name.trim();
  if (!n) throw new Error("Folder name is required");
  return { ...folder, name: n, updatedAt: ctx.now };
}

/**
 * Returns true when making `folderId` a child of `newParentId` would create a cycle,
 * i.e. `newParentId` is `folderId` or one of its descendants.
 */
export function wouldCreateCycle(
  folders: readonly Folder[],
  folderId: string,
  newParentId: string | null,
): boolean {
  if (newParentId === null) return false;
  if (newParentId === folderId) return true;
  const byId = new Map(folders.map((f) => [f.id, f]));
  const seen = new Set<string>();
  let cursor: string | undefined = newParentId;
  while (cursor) {
    if (cursor === folderId) return true;
    if (seen.has(cursor)) return true; // pre-existing cycle; refuse to make it worse
    seen.add(cursor);
    cursor = byId.get(cursor)?.parentId;
  }
  return false;
}

export function moveFolder(
  folder: Folder,
  newParentId: string | null,
  allFolders: readonly Folder[],
  ctx: NowContext,
): Folder {
  if (wouldCreateCycle(allFolders, folder.id, newParentId)) {
    throw new Error("A folder cannot be moved inside itself");
  }
  const next: Folder = { ...folder, updatedAt: ctx.now };
  if (newParentId === null) delete next.parentId;
  else next.parentId = newParentId;
  return next;
}

export function reorderFolder(folder: Folder, sortOrder: number, ctx: NowContext): Folder {
  return { ...folder, sortOrder, updatedAt: ctx.now };
}

export function deleteFolder(folder: Folder, ctx: NowContext): Folder {
  return { ...folder, deletedAt: ctx.now, updatedAt: ctx.now };
}

export function restoreFolder(folder: Folder, ctx: NowContext): Folder {
  const next: Folder = { ...folder, updatedAt: ctx.now };
  delete next.deletedAt;
  return next;
}

export type FolderTreeNode = { folder: Folder; children: FolderTreeNode[] };

/** Nested tree ordered by `sortOrder` then name. Orphans (parent missing) become roots. */
export function buildFolderTree(folders: readonly Folder[]): FolderTreeNode[] {
  const ids = new Set(folders.map((f) => f.id));
  const childrenOf = new Map<string | undefined, Folder[]>();
  for (const f of folders) {
    const key = f.parentId && ids.has(f.parentId) ? f.parentId : undefined;
    const list = childrenOf.get(key) ?? [];
    list.push(f);
    childrenOf.set(key, list);
  }
  const order = (a: Folder, b: Folder) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);
  const build = (parent: string | undefined, depth: number): FolderTreeNode[] =>
    depth > 64
      ? []
      : (childrenOf.get(parent) ?? [])
          .sort(order)
          .map((folder) => ({ folder, children: build(folder.id, depth + 1) }));
  return build(undefined, 0);
}

/** `folderId` and every folder beneath it. */
export function descendantFolderIds(folders: readonly Folder[], folderId: string): Set<string> {
  const out = new Set<string>([folderId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const f of folders) {
      if (f.parentId && out.has(f.parentId) && !out.has(f.id)) {
        out.add(f.id);
        grew = true;
      }
    }
  }
  return out;
}

export function folderPath(folders: readonly Folder[], folderId: string): Folder[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const path: Folder[] = [];
  let cursor = byId.get(folderId);
  const seen = new Set<string>();
  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    path.unshift(cursor);
    cursor = cursor.parentId ? byId.get(cursor.parentId) : undefined;
  }
  return path;
}
